import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { OfflineProvider } from './contexts/OfflineContext';
import { Toaster } from './components/ui/sonner';
import SplashScreen from './components/SplashScreen';

// Auth Pages
import Login from './pages/Login';
import FirstLoginReset from './pages/FirstLoginReset';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// SuperAdmin Pages
import SuperAdminDashboard from './pages/SuperAdmin/Dashboard';
import SuperAdminCompanies from './pages/SuperAdmin/Companies';
import SuperAdminUsers from './pages/SuperAdmin/Users';
import SuperAdminSettings from './pages/SuperAdmin/Settings';

// CompanyAdmin Pages
import CompanyAdminDashboard from './pages/CompanyAdmin/Dashboard';
import CompanyAdminProfile from './pages/CompanyAdmin/Profile';
import CompanyAdminUsers from './pages/CompanyAdmin/Users';
import CompanyAdminProjects from './pages/CompanyAdmin/Projects';
import CompanyAdminAnalytics from './pages/CompanyAdmin/Analytics';
import CompanyAdminIntegrations from './pages/CompanyAdmin/Integrations';
import CompanyAdminAuditLog from './pages/CompanyAdmin/AuditLog';
import CompanyAdminSettings from './pages/CompanyAdmin/Settings';
import ConstructionStructure from './pages/CompanyAdmin/ConstructionStructure';
import Portfolios from './pages/CompanyAdmin/Portfolios';
import PortfolioDetail from './pages/CompanyAdmin/PortfolioDetail';
import CommandCenter from './pages/Portfolio/CommandCenter';
import PortfolioMyDay from './pages/Portfolio/PortfolioMyDay';
import IntegrationHub from './pages/Admin/IntegrationHub';
import BOQIntelligence from './pages/User/BOQIntelligence';
import BOQImport from './pages/User/BOQImport';
import ScheduleIntelligence from './pages/User/ScheduleIntelligence';
import ScheduleImport from './pages/User/ScheduleImport';
import ProgressIntelligence from './pages/User/ProgressIntelligence';
import DesignIntelligence from './pages/User/DesignIntelligence';
import QualityIntelligence from './pages/User/QualityIntelligence';
import SiteWalks from './pages/User/SiteWalks';
import SiteWalkDetail from './pages/User/SiteWalkDetail';
import SiteWalkOps from './pages/User/SiteWalkOps';
import ConstructionControls from './pages/User/ConstructionControls';
import SafetyControls from './pages/User/SafetyControls';
import ProjectWorkspace from './pages/User/ProjectWorkspace';
import PrivacyComplianceCenter from './pages/Admin/PrivacyComplianceCenter';
import PrivacyMyData from './pages/User/PrivacyMyData';
import SafetyIntelligence from './pages/User/SafetyIntelligence';

// User Pages
import UserDashboard from './pages/User/Dashboard';
import UserProjects from './pages/User/Projects';
import Uploads from './pages/User/Uploads';
import ObjectIntelligence from './pages/User/ObjectIntelligence';
import Measure2D from './pages/User/Measure2D';
import Measure3D from './pages/User/Measure3D';
import ReferenceDrawing from './pages/User/ReferenceDrawing';
import Issues from './pages/User/Issues';
import MyIssues from './pages/User/MyIssues';
import CostCodes from './pages/User/CostCodes';
import Reports from './pages/User/Reports';
import VersionHistory from './pages/User/VersionHistory';
import Assistant from './pages/User/Assistant';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Defense-in-depth: temp-password users must set their own password first.
  if (user.firstLoginRequired) {
    return <Navigate to="/first-login-reset" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <OfflineProvider>
      <SplashScreen />
      <BrowserRouter>
        <Toaster position="top-right" />
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/first-login-reset" element={<FirstLoginReset />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* SuperAdmin Routes */}
          <Route
            path="/superadmin/dashboard"
            element={
              <ProtectedRoute allowedRoles={['SuperAdmin']}>
                <SuperAdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/superadmin/companies"
            element={
              <ProtectedRoute allowedRoles={['SuperAdmin']}>
                <SuperAdminCompanies />
              </ProtectedRoute>
            }
          />
          <Route
            path="/superadmin/users"
            element={
              <ProtectedRoute allowedRoles={['SuperAdmin']}>
                <SuperAdminUsers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/superadmin/settings"
            element={
              <ProtectedRoute allowedRoles={['SuperAdmin']}>
                <SuperAdminSettings />
              </ProtectedRoute>
            }
          />

          {/* CompanyAdmin Routes */}
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute allowedRoles={['CompanyAdmin']}>
                <CompanyAdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/profile"
            element={
              <ProtectedRoute allowedRoles={['CompanyAdmin']}>
                <CompanyAdminProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute allowedRoles={['CompanyAdmin']}>
                <CompanyAdminUsers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/projects"
            element={
              <ProtectedRoute allowedRoles={['CompanyAdmin']}>
                <CompanyAdminProjects />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute allowedRoles={['CompanyAdmin']}>
                <CompanyAdminAnalytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/integrations"
            element={
              <ProtectedRoute allowedRoles={['CompanyAdmin']}>
                <CompanyAdminIntegrations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/audit-logs"
            element={
              <ProtectedRoute allowedRoles={['CompanyAdmin']}>
                <CompanyAdminAuditLog />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute allowedRoles={['CompanyAdmin']}>
                <CompanyAdminSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/privacy"
            element={
              <ProtectedRoute allowedRoles={['CompanyAdmin']}>
                <PrivacyComplianceCenter />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/portfolios"
            element={
              <ProtectedRoute allowedRoles={['CompanyAdmin']}>
                <Portfolios />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/integration-hub"
            element={
              <ProtectedRoute allowedRoles={['CompanyAdmin']}>
                <IntegrationHub />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/portfolios/:portfolioId"
            element={
              <ProtectedRoute allowedRoles={['CompanyAdmin']}>
                <PortfolioDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/portfolio/:portfolioId/command"
            element={
              <ProtectedRoute>
                <CommandCenter />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/portfolio/:portfolioId/my-day"
            element={
              <ProtectedRoute>
                <PortfolioMyDay />
              </ProtectedRoute>
            }
          />

          {/* User Routes */}
          <Route
            path="/app/dashboard"
            element={
              <ProtectedRoute allowedRoles={['User', 'CompanyAdmin']}>
                <UserDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/projects"
            element={
              <ProtectedRoute allowedRoles={['User', 'CompanyAdmin']}>
                <UserProjects />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/projects/:projectId/workspace"
            element={
              <ProtectedRoute allowedRoles={['User', 'CompanyAdmin']}>
                <ProjectWorkspace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/projects/:projectId/uploads"
            element={
              <ProtectedRoute allowedRoles={['User', 'CompanyAdmin']}>
                <Uploads />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/projects/:projectId/object-intel"
            element={
              <ProtectedRoute allowedRoles={['User', 'CompanyAdmin']}>
                <ObjectIntelligence />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/projects/:projectId/measure"
            element={
              <ProtectedRoute allowedRoles={['User', 'CompanyAdmin']}>
                <Measure2D />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/projects/:projectId/3d"
            element={
              <ProtectedRoute allowedRoles={['User', 'CompanyAdmin']}>
                <Measure3D />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/projects/:projectId/reference-drawing"
            element={
              <ProtectedRoute allowedRoles={['User', 'CompanyAdmin']}>
                <ReferenceDrawing />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/projects/:projectId/reports"
            element={
              <ProtectedRoute allowedRoles={['User', 'CompanyAdmin']}>
                <Reports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/projects/:projectId/issues"
            element={
              <ProtectedRoute allowedRoles={['User', 'CompanyAdmin']}>
                <Issues />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/my-issues"
            element={
              <ProtectedRoute allowedRoles={['User', 'CompanyAdmin']}>
                <MyIssues />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/projects/:projectId/cost-codes"
            element={
              <ProtectedRoute allowedRoles={['User', 'CompanyAdmin']}>
                <CostCodes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/projects/:projectId/history"
            element={
              <ProtectedRoute allowedRoles={['User', 'CompanyAdmin']}>
                <VersionHistory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/projects/:projectId/construction"
            element={
              <ProtectedRoute allowedRoles={['CompanyAdmin']}>
                <ConstructionStructure />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/projects/:projectId/boq"
            element={
              <ProtectedRoute allowedRoles={['User', 'CompanyAdmin']}>
                <BOQIntelligence />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/projects/:projectId/boq-import"
            element={
              <ProtectedRoute allowedRoles={['CompanyAdmin']}>
                <BOQImport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/projects/:projectId/schedule"
            element={
              <ProtectedRoute allowedRoles={['User', 'CompanyAdmin']}>
                <ScheduleIntelligence />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/projects/:projectId/schedule-import"
            element={
              <ProtectedRoute allowedRoles={['CompanyAdmin']}>
                <ScheduleImport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/projects/:projectId/progress"
            element={
              <ProtectedRoute allowedRoles={['User', 'CompanyAdmin']}>
                <ProgressIntelligence />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/projects/:projectId/design"
            element={
              <ProtectedRoute allowedRoles={['User', 'CompanyAdmin']}>
                <DesignIntelligence />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/projects/:projectId/quality"
            element={
              <ProtectedRoute allowedRoles={['User', 'CompanyAdmin']}>
                <QualityIntelligence />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/projects/:projectId/safety"
            element={
              <ProtectedRoute allowedRoles={['User', 'CompanyAdmin']}>
                <SafetyIntelligence />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/projects/:projectId/site-walks"
            element={
              <ProtectedRoute allowedRoles={['User', 'CompanyAdmin']}>
                <SiteWalks />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/projects/:projectId/site-walk-ops"
            element={
              <ProtectedRoute allowedRoles={['User', 'CompanyAdmin']}>
                <SiteWalkOps />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/projects/:projectId/controls"
            element={
              <ProtectedRoute allowedRoles={['User', 'CompanyAdmin']}>
                <ConstructionControls />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/projects/:projectId/safety-controls"
            element={
              <ProtectedRoute allowedRoles={['User', 'CompanyAdmin']}>
                <SafetyControls />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/projects/:projectId/site-walks/:walkId"
            element={
              <ProtectedRoute allowedRoles={['User', 'CompanyAdmin']}>
                <SiteWalkDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/assistant"
            element={
              <ProtectedRoute allowedRoles={['User', 'CompanyAdmin']}>
                <Assistant />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/privacy"
            element={
              <ProtectedRoute allowedRoles={['User', 'CompanyAdmin']}>
                <PrivacyMyData />
              </ProtectedRoute>
            }
          />

          {/* Default Route */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
      </OfflineProvider>
    </AuthProvider>
  );
}

export default App;
